<template>
  <div class="w-100 h-100">
    <div class="px-6 py-6 contact-form-container d-flex flex-column border-radius-4">
      <transition
          name="fade"
          mode="out-in"
      >
        <template v-if="isSendCompleted">
          <div
            data-test="successContainer"
            class="success-container p-4 border-radius-4 d-flex"
          >
            <div class="d-flex align-items-center">
              <VIcon color="textWhite">
                mdi-check-circle
              </VIcon>
            </div>
            <div class="pl-4">
              <span class="d-flex flex-column">
                <span class="body-1 textWhite--text">
                  {{ successModal.successMessage }}
                </span>
                <span
                    class="body-1 textWhite--text send-another-feedback-btn"
                    data-test="sendAnotherMessage"
                    @click="toResendForm"
                >
                  {{ successModal.sendAnotherMessageBtnText || 'Send another message' }}
                </span>
              </span>
            </div>
          </div>
        </template>
        <template v-else>
          <ValidationObserver
              ref="contactUsForm"
              name="contactUsForm"
              data-test="contactUsForm"
              tag="form"
          >
            <ValidationProvider
                v-slot="{ errors, ariaInput }"
                name="fullName"
                :rules="fullNameRules"
                mode="aggressive"
            >
              <VTextField
                  v-bind="ariaInput"
                  v-model="fullName"
                  type="text"
                  data-test="contactUs.fullName"
                  :label="contactForm.fullName"
                  :error="!!errors.length"
                  :error-messages="$t(errors[0])"
                  :hide-details="!errors.length"
                  class="body-1 pt-0"
              />
            </ValidationProvider>
            <ValidationProvider
                v-slot="{ errors, ariaInput }"
                data-test="contactUs.email"
                name="email"
                :rules="emailRules"
                mode="eager"
            >
              <VTextField
                  v-bind="ariaInput"
                  v-model="email"
                  type="text"
                  :label="contactForm.email"
                  :hide-details="!errors.length"
                  :error="!!errors.length"
                  :error-messages="$t(errors[0])"
                  class="body-1 pt-0"
              />
            </ValidationProvider>
            <ValidationProvider
                v-slot="{ errors, ariaInput }"
                name="phone"
                :rules="phoneRules"
                mode="eager"
            >
              <!-- todo: default country must be approved by BA -->
              <vue-tel-input-vuetify
                ref="phoneInput"
                v-bind="ariaInput"
                :value="typeof phone === 'string' ? phone : ''"
                v-phone-restrict
                class="body-1"
                defaultCountry="LU"
                :error="!!errors.length"
                :error-messages="errors[0]"
                :placeholder="`${contactForm.phone}`"
                :label="`${contactForm.phone}`"
                disabledFetchingCountry
                @input="onPhoneInput"
                @country-changed="countryChanged"
              />
            </ValidationProvider>
            <ValidationProvider
                v-slot="{ errors, ariaInput }"
                name="message"
                :rules="{ required: true, messageMaxCounts: 500 }"
                mode="aggressive"
            >
              <VTextarea
                v-bind="ariaInput"
                v-model="message"
                type="text"
                data-test="contactUs.message"
                :label="contactForm.message"
                no-resize
                rows="1"
                auto-grow
                :error="!!errors.length"
                :error-messages="$t(errors[0])"
                class="body-1 pt-0"
              />
            </ValidationProvider>
            <div class="pb-3">
              <VueRecaptcha
                  :sitekey="siteKey"
                  data-test="contactUs.recaptcha"
                  @verify="onRecaptchaVerify"
                  @expired="onRecaptchaExpired"
              />
            </div>
            <VBtn
                v-blur
                large
                dark
                data-test="contactUs.submitBtn"
                class="border-radius-8 elevation-4 w-100"
                color="brandingPrimary"
                :class="{'disabled': !isValidForm}"
                @click="sendForm"
            >
              <span class="button">
                {{ `${contactForm.send}` }}
              </span>
            </VBtn>
          </ValidationObserver>
        </template>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'ContactUsForm',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { extend } from 'vee-validate';
import StrapiService from '@slg/web-customer-shared/src/services/StrapiService';
import { max, email as emailRule, regex } from 'vee-validate/dist/rules';
import VueRecaptcha from 'vue-recaptcha';
import Constants from '@slg/web-customer-shared/src/utils/constants';
import { phoneFromTelInput } from '@slg/web-customer-shared/src/utils/telInput';
import { useGlobalErrorDialog } from '../../composables';

const props = defineProps({
  successModal: {
    type: Object,
    default: () => ({}),
  },
  contactForm: {
    type: Object,
    default: () => ({}),
  },
  validationErrors: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['setGlobalLoading']);
const { setGlobalError } = useGlobalErrorDialog();

const fullName = ref('');
const email = ref('');
const phone = ref('');
const message = ref('');
const siteKey = config.VUE_APP_SITE_KEY || process.env.VUE_APP_SITE_KEY;
const recaptchaResolvedKey = ref('');
const isSendCompleted = ref(false);

const phoneInput = ref<InstanceType<typeof VueRecaptcha> | HTMLElement | { phoneObject?: { isValid?: boolean; country?: { dialCode?: string } } } | null>(null);
const contactUsForm = ref<InstanceType<typeof VueRecaptcha> | HTMLElement | { errors?: Record<string, unknown[]> } | null>(null);

const isValidPhoneNumber = computed(() => {
  if (typeof phone.value === 'string' && phone.value && phoneInput.value) {
    return Boolean((phoneInput.value as { phoneObject?: { isValid?: boolean } }).phoneObject?.isValid);
  }
  return true;
});

const fullNameRules = {
  required: true,
  maxFullName: 255,
  onlyLettersAndSymbols: Constants.FIRST_SECOND_NAME_REGEX,
};

const emailRules = {
  required: true,
  maxEmail: 255,
  email: true,
};

const phoneRules = computed(() => ({
  phoneNumber: {
    isValidPhoneNumber: isValidPhoneNumber.value,
  },
}));

const isValidForm = computed(() => {
  if (fullName.value && email.value && message.value) {
    const contactUsFormObserver = (contactUsForm.value as { errors: Record<string, unknown[]> }).errors;
    return recaptchaResolvedKey.value && Object.keys(contactUsFormObserver).every((key) => {
      if (key !== 'phone') {
        return !contactUsFormObserver[key].length;
      }
      return true;
    });
  }
  return false;
});

const getPhoneObject = computed(() => (
  phoneInput.value as { phoneObject?: { country?: { dialCode?: string } } } | null
)?.phoneObject);

function clearForm() {
  fullName.value = '';
  email.value = '';
  phone.value = '';
  message.value = '';
  recaptchaResolvedKey.value = '';
}

function onPhoneInput(value: unknown) {
  phone.value = phoneFromTelInput(value);
}

function countryChanged() {
  const dialCode = getPhoneObject.value?.country?.dialCode;
  if (dialCode) {
    phone.value = `+${dialCode}`;
  }
}

function toResendForm() {
  isSendCompleted.value = false;
  clearForm();
}

function validationInitialization() {
  extend('maxFullName', {
    ...max,
    message: () => props.validationErrors.fullNameMax255,
  });
  extend('onlyLettersAndSymbols', {
    ...regex,
    message: () => props.validationErrors.numbersAndSymbols,
  });

  extend('email', {
    ...emailRule,
    message: () => props.validationErrors.emailRegex,
  });
  extend('maxEmail', {
    ...max,
    message: () => props.validationErrors.emailMax255,
  });

  extend('phoneNumber', {
    validate: () => isValidPhoneNumber.value,
    message: () => props.validationErrors.phoneRegex,
  });

  extend('messageMaxCounts', {
    ...max,
    message: () => props.validationErrors.messageMax500,
  });
}

function onRecaptchaVerify(e: string) {
  recaptchaResolvedKey.value = e;
}

function onRecaptchaExpired() {
  recaptchaResolvedKey.value = '';
}

async function sendForm() {
  const dataToSend = {
    fullName: fullName.value,
    email: email.value,
    phone: isValidPhoneNumber.value ? phone.value : '',
    message: message.value,
  };
  try {
    emit('setGlobalLoading', true);
    await StrapiService.sendContactUsFeedback(dataToSend);
    emit('setGlobalLoading', false);
    isSendCompleted.value = true;
  } catch (e) {
    emit('setGlobalLoading', false);
    await setGlobalError(e as { errorCode?: string; message?: string });
  }
}

validationInitialization();
</script>

<style scoped lang="scss">
.contact-form-container {
  background-color: var(--v-textWhite-base) !important;
}

.success-container {
  background-color: var(--v-success-base) !important;
}

.send-another-feedback-btn {
  cursor: pointer;
  text-decoration: underline;
}
</style>
